import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Paper,
  Switch,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSectionStyles } from '@/styles/utilities/hooks';
import { DraggableDialog } from '@/shared/components/DraggableDialog';
import FindReplacePanelAlert from '../components/FindReplacePanelAlert';
import {
  substituteFieldPlaceholders,
  validateFieldPlaceholders,
  DISPLAY_FIELDS,
  getSampleReplacement
} from '../utils/fieldSubstitution';

// Fields that can be searched and replaced
const SEARCHABLE_FIELDS = [
  { id: 'project', label: 'Project' },
  { id: 'target', label: 'Target' },
  { id: 'mutation', label: 'Mutation' },
  { id: 'subgroup', label: 'Subgroup' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'batch', label: 'Batch' },
  { id: 'ligand_name', label: 'Ligand Name' },
  { id: 'ligand_tag', label: 'Ligand Tag' },
  { id: 'author', label: 'Author' },
  { id: 'notes', label: 'Notes' },
  { id: 'origin_name', label: 'Origin Name' },
];

const FindReplacePanel = forwardRef(({
  selectedRows = [],
  uploadFiles = [],
  selectedUploadRows = [],
  onPreviewUpdate,
  previewMode = false,
  allPrepFiles = [],
}, ref) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();

  // State management
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [useRegex, setUseRegex] = useState(false);
  const [matchCase, setMatchCase] = useState(false);
  const [selectedFields, setSelectedFields] = useState(new Set());
  const [regexError, setRegexError] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const replaceInputRef = useRef(null);

  // Combine selected rows and uploaded files for processing
  const combinedRows = useMemo(() => {
    const rows = [...selectedRows];

    // Add upload files if selected
    const uploadRowsToAdd = uploadFiles.filter(file =>
      selectedUploadRows.includes(file.id)
    );

    uploadRowsToAdd.forEach(file => {
      rows.push({
        id: file.id,
        project: file.project || '',
        target: file.target || '',
        mutation: file.mutation || '',
        subgroup: file.subgroup || '',
        workflow: file.workflow || '',
        batch: file.batch || '',
        ligand_name: file.ligand_name || '',
        ligand_tag: file.ligand_tag || '',
        author: file.author || '',
        notes: file.notes || '',
        origin_name: file.name || '',
        isUpload: true,
      });
    });

    return rows;
  }, [selectedRows, uploadFiles, selectedUploadRows]);

  // Reset function exposed via ref
  const reset = useCallback(() => {
    setFindText('');
    setReplaceText('');
    setUseRegex(false);
    setMatchCase(false);
    setSelectedFields(new Set());
    setRegexError('');
    setMatchCount(0);
    onPreviewUpdate(false, [], 'findreplace');
  }, [onPreviewUpdate]);

  // Toggle help
  const toggleHelp = useCallback(() => {
    setShowHelp(prev => !prev);
  }, []);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    reset,
    toggleHelp,
  }), [reset, toggleHelp]);

  // Validate regex pattern
  const validateRegex = useCallback((pattern) => {
    if (!useRegex) return true;
    try {
      new RegExp(pattern, matchCase ? '' : 'i');
      setRegexError('');
      return true;
    } catch (e) {
      setRegexError(e.message);
      return false;
    }
  }, [useRegex, matchCase]);

  // Calculate matches and preview data
  const calculatePreview = useCallback(() => {
    if (!findText || selectedFields.size === 0 || combinedRows.length === 0) {
      setMatchCount(0);
      return [];
    }

    if (useRegex && !validateRegex(findText)) {
      setMatchCount(0);
      return [];
    }

    let totalMatches = 0;
    const previewData = [];

    combinedRows.forEach(row => {
      const updatedRow = { id: row.id };
      let rowHasChanges = false;

      Array.from(selectedFields).forEach(field => {
        const originalValue = String(row[field] || '');
        let newValue = originalValue;
        let fieldMatches = 0;

        if (useRegex) {
          try {
            const regex = new RegExp(findText, matchCase ? 'g' : 'gi');
            const matches = originalValue.match(regex);
            fieldMatches = matches ? matches.length : 0;
            if (fieldMatches > 0) {
              // For regex mode, first do the regex replacement
              newValue = originalValue.replace(regex, (match, ...groups) => {
                // Create a temporary replacement with capture groups
                let tempReplacement = replaceText;

                // Replace capture groups $1, $2, etc.
                groups.forEach((group, index) => {
                  if (group !== undefined && index < groups.length - 2) { // Last 2 are offset and full string
                    tempReplacement = tempReplacement.replace(new RegExp('\\\\$' + (index + 1), 'g'), group || '');
                  }
                });

                // Now substitute field placeholders with actual row values
                return substituteFieldPlaceholders(tempReplacement, row, { emptyValue: '' });
              });
            }
          } catch (e) {
            // Regex error already handled by validation
          }
        } else {
          // Text search
          const searchStr = matchCase ? findText : findText.toLowerCase();
          const valueStr = matchCase ? originalValue : originalValue.toLowerCase();
          let pos = 0;
          while ((pos = valueStr.indexOf(searchStr, pos)) !== -1) {
            fieldMatches++;
            pos += searchStr.length;
          }

          if (fieldMatches > 0) {
            // Replace all occurrences
            const regex = new RegExp(
              findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
              matchCase ? 'g' : 'gi'
            );
            newValue = originalValue.replace(regex, replaceText);
          }
        }

        if (fieldMatches > 0) {
          totalMatches += fieldMatches;
          updatedRow[field] = newValue;
          rowHasChanges = true;
        }
      });

      if (rowHasChanges) {
        previewData.push(updatedRow);
      }
    });

    setMatchCount(totalMatches);
    return previewData;
  }, [findText, replaceText, useRegex, matchCase, selectedFields, combinedRows, validateRegex]);

  // Update preview when parameters change
  useEffect(() => {
    // Validate field placeholders in replace text
    const validation = validateFieldPlaceholders(replaceText);
    if (!validation.isValid && replaceText) {
      setFieldError(`Invalid field(s): ${validation.invalidFields.join(', ')}`);
    } else {
      setFieldError('');
    }

    if (findText && selectedFields.size > 0) {
      const preview = calculatePreview();
      onPreviewUpdate(preview.length > 0, preview, 'findreplace');
    } else {
      onPreviewUpdate(false, [], 'findreplace');
      setMatchCount(0);
    }
  }, [findText, replaceText, useRegex, matchCase, selectedFields, calculatePreview, onPreviewUpdate]);

  // Handle field selection
  const handleFieldToggle = (fieldId) => {
    setSelectedFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fieldId)) {
        newSet.delete(fieldId);
      } else {
        newSet.add(fieldId);
      }
      return newSet;
    });
  };

  // Select all fields
  const handleSelectAll = () => {
    if (selectedFields.size === SEARCHABLE_FIELDS.length) {
      setSelectedFields(new Set());
    } else {
      setSelectedFields(new Set(SEARCHABLE_FIELDS.map(f => f.id)));
    }
  };

  // Insert field placeholder at cursor position
  const insertFieldPlaceholder = useCallback((fieldName) => {
    if (!replaceInputRef.current) return;

    const input = replaceInputRef.current.querySelector('input') || replaceInputRef.current;
    const start = input.selectionStart || replaceText.length;
    const end = input.selectionEnd || replaceText.length;

    const newText = replaceText.substring(0, start) + `{${fieldName}}` + replaceText.substring(end);
    setReplaceText(newText);

    // Set cursor position after the inserted placeholder
    setTimeout(() => {
      if (input) {
        const newPosition = start + fieldName.length + 2; // +2 for the curly braces
        input.setSelectionRange(newPosition, newPosition);
        input.focus();
      }
    }, 0);
  }, [replaceText]);

  // Get sample replacement text
  const sampleReplacement = useMemo(() => {
    if (!replaceText || combinedRows.length === 0) return '';
    return getSampleReplacement(replaceText, combinedRows[0]);
  }, [replaceText, combinedRows]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Help Dialog */}
      <DraggableDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title="Find and Replace Help"
        variant="help"
        maxWidth="sm"
        fullWidth
      >
        <Box>
          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Basic Usage:
          </Typography>
          <Typography component="div" sx={{ mb: theme.spacing(2), fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Enter text to find across selected fields<br />
            • Enter replacement text (leave empty to delete)<br />
            • Select which fields to search in<br />
            • Preview shows all changes before applying
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Text Search:
          </Typography>
          <Typography component="div" sx={{ mb: theme.spacing(2), fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Simple text matching (default mode)<br />
            • Use "Match Case" for case-sensitive search<br />
            • All occurrences in a field are replaced<br />
            • Example: Find "Ver2" → Replace with "ver2"
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Regular Expressions:
          </Typography>
          <Typography component="div" sx={{ mb: theme.spacing(2), fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Enable "Use Regex" for pattern matching<br />
            • Use capture groups: <code>(pattern)</code> and <code>$1</code>, <code>$2</code> in replacement<br />
            • Example: Find <code>^(\w+)_v2$</code> → Replace with <code>$1-ver2</code><br />
            • Common patterns: <code>\d+</code> (digits), <code>\w+</code> (words), <code>.*</code> (anything)
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Field Value Substitution:
          </Typography>
          <Typography component="div" sx={{ mb: theme.spacing(2), fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Use <code>{'{field_name}'}</code> to insert field values in replacement<br />
            • Combine with regex capture groups: <code>$1-{'{workflow}'}-$2</code><br />
            • Example: Find <code>(.*)-(.*)$</code> → Replace <code>$1-{'{workflow}'}-$2</code><br />
            • Transforms "xTB-set0" to "xTB-FMO-set0" if workflow="FMO"<br />
            • Click field chips below replace input to insert placeholders<br />
            • Use <code>\{'{field}'}\</code> for literal curly braces
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Field Selection:
          </Typography>
          <Typography component="div" sx={{ mb: theme.spacing(2), fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Search only in selected fields<br />
            • Use "Select All" to quickly choose all fields<br />
            • Different fields can have different content<br />
            • Changes apply only to fields with matches
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Applying Changes:
          </Typography>
          <Typography component="div" sx={{ fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Changes preview automatically as you type<br />
            • Match count shows total replacements<br />
            • Click "Save" in main toolbar to apply changes<br />
            • Click "Cancel" to discard all changes
          </Typography>
        </Box>
      </DraggableDialog>

      {/* Alert showing selection info */}
      <Box sx={{ mx: theme.spacing(2), mt: theme.spacing(2), mb: theme.spacing(1.5) }}>
        <FindReplacePanelAlert
          selectedRows={selectedRows}
          selectedUploadRows={selectedUploadRows}
          matchCount={matchCount}
        />
      </Box>

      {/* Scrollable Content Container */}
      <Box sx={{ flex: 1, overflow: 'auto', px: theme.spacing(2), pb: theme.spacing(2) }}>

        {/* SEARCH CRITERIA Section */}
        <Box sx={{ mb: theme.spacing(2) }}>
          <Typography variant="caption" sx={{ ...sectionStyles.sectionHeader, mb: theme.spacing(1) }}>
            SEARCH CRITERIA
          </Typography>

          {/* Find input */}
          <Box sx={{ mb: theme.spacing(1.5) }}>
            <TextField
              fullWidth
              size="small"
              label="Find"
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
              placeholder={useRegex ? "Enter regex pattern..." : "Enter text to find..."}
              error={!!regexError}
              helperText={regexError || (useRegex ? "Using regular expression mode" : "Using text search mode")}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Replace input */}
          <Box sx={{ mb: theme.spacing(1.5) }}>
            <TextField
              fullWidth
              size="small"
              label="Replace with"
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              placeholder={useRegex ? "Use $1, $2 for groups, {field} for values" : "Enter text or use {field} for values"}
              error={!!fieldError}
              helperText={fieldError || (sampleReplacement && replaceText !== sampleReplacement ? `Example: ${sampleReplacement}` : '')}
              InputLabelProps={{ shrink: true }}
              ref={replaceInputRef}
            />
          </Box>

          {/* Field placeholders for easy insertion */}
          <Box sx={{ mb: theme.spacing(1.5) }}>
            <Typography variant="caption" sx={{ fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary, display: 'block', mb: theme.spacing(0.5) }}>
              Click to insert field value:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.5) }}>
              {DISPLAY_FIELDS.map(field => (
                <Chip
                  key={field.name}
                  label={field.label}
                  size="small"
                  variant="outlined"
                  onClick={() => insertFieldPlaceholder(field.name)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: theme.typography.fontSize.xs,
                    height: theme.spacing(2.5),
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Options */}
          <Box sx={{ display: 'flex', gap: theme.spacing(2), alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useRegex}
                  onChange={(e) => setUseRegex(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography sx={{ fontSize: theme.typography.fontSize.sm }}>Use Regex</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={matchCase}
                  onChange={(e) => setMatchCase(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography sx={{ fontSize: theme.typography.fontSize.sm }}>Match Case</Typography>}
            />
            {matchCount > 0 && (
              <Chip
                label={`${matchCount} match${matchCount !== 1 ? 'es' : ''} found`}
                color="primary"
                size="small"
                variant="outlined"
                sx={{ ml: 'auto' }}
              />
            )}
          </Box>
        </Box>

        {/* FIELD SELECTION Section */}
        <Box sx={{ mt: theme.spacing(2) }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: theme.spacing(1) }}>
            <Typography variant="caption" sx={{ ...sectionStyles.sectionHeader }}>
              FIELD SELECTION
            </Typography>
            <Typography
              variant="caption"
              onClick={handleSelectAll}
              sx={{
                color: theme.palette.primary.main,
                cursor: 'pointer',
                fontSize: theme.typography.fontSize.sm,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {selectedFields.size === SEARCHABLE_FIELDS.length ? 'Clear All' : 'Select All'}
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: theme.spacing(1.5) }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
              {SEARCHABLE_FIELDS.map(field => (
                <FormControlLabel
                  key={field.id}
                  control={
                    <Checkbox
                      checked={selectedFields.has(field.id)}
                      onChange={() => handleFieldToggle(field.id)}
                      size="small"
                    />
                  }
                  label={<Typography sx={{ fontSize: theme.typography.fontSize.sm }}>{field.label}</Typography>}
                  sx={{ m: 0 }}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
});

FindReplacePanel.displayName = 'FindReplacePanel';

export default FindReplacePanel;